import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Clock, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ScrollArea, ScrollBar } from '../ui/scroll-area';
import axios from 'axios';
import { sortEventsByDateDesc } from '../../utils/eventsSource';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UpcomingEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`, {
          params: { upcoming_only: false, limit: 50 },
        });
        setEvents(sortEventsByDateDesc(response.data).slice(0, 6));
      } catch (error) {
        console.error('Failed to fetch events:', error);
        // Fallback data
        setEvents([
          {
            id: '1',
            title: 'Centenary Celebration Walk',
            description: 'Join us for a guided countryside walk celebrating 100 years of CPRE.',
            location: 'Rutland Water Visitor Centre',
            event_date: '2026-03-15T10:00:00Z',
            image_url: 'https://images.unsplash.com/photo-1759938894506-965368ace0f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
          },
          {
            id: '2',
            title: 'Spring Wildlife Survey Training',
            description: 'Learn how to conduct wildlife surveys and contribute to citizen science.',
            location: 'Lincoln Nature Centre',
            event_date: '2026-04-05T14:00:00Z',
            image_url: 'https://images.unsplash.com/photo-1719909871645-cc4977336274?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
          },
          {
            id: '3',
            title: 'Annual General Meeting 2026',
            description: 'Our AGM is open to all members. Hear about achievements and future plans.',
            location: 'Oakham Castle Hall',
            event_date: '2026-05-20T18:30:00Z',
            image_url: '/images/cpre images for website/29.jpg',
          },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString('en-GB', { month: 'short' }),
      time: date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  return (
    <section className="section-padding bg-countryside-bone/50" data-testid="upcoming-events">
      <div className="container-custom">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <span className="text-countryside-terracotta font-medium text-sm uppercase tracking-wider">Join Us</span>
            <h2 className="mt-2">Upcoming Events</h2>
            <p className="text-muted-foreground mt-2 max-w-xl">
              Discover opportunities to connect with nature and fellow countryside enthusiasts.
            </p>
          </div>
          <Link to="/events">
            <Button 
              variant="ghost" 
              className="text-countryside-green hover:text-countryside-green-dark"
              data-testid="view-all-events-link"
            >
              View All Events
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Events Horizontal Scroll */}
        <ScrollArea className="w-full whitespace-nowrap">
          <div className="flex gap-6 pb-4">
            {events.map((event, index) => {
              const dateInfo = formatDate(event.event_date);
              return (
                <Card 
                  key={event.id}
                  className="bg-white border-stone-200 w-[340px] flex-shrink-0 overflow-hidden group hover:shadow-lg transition-shadow duration-300"
                  data-testid={`event-card-${index}`}
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={event.image_url || 'https://images.unsplash.com/photo-1759938894506-965368ace0f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=400'}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {/* Date Badge */}
                    <div className="absolute top-4 left-4 bg-white rounded-xl px-4 py-2 text-center shadow-md">
                      <span className="block text-2xl font-heading font-bold text-countryside-green">{dateInfo.day}</span>
                      <span className="block text-xs font-medium text-muted-foreground uppercase">{dateInfo.month}</span>
                    </div>
                  </div>
                  <CardContent className="p-5">
                    <h3 className="font-heading font-semibold text-lg text-foreground mb-2 whitespace-normal line-clamp-1">
                      {event.title}
                    </h3>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-countryside-green" />
                        <span className="whitespace-normal line-clamp-1">{event.location}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-countryside-green" />
                        <span>{dateInfo.time}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    </section>
  );
};
